<template>
  <div :class="['showcase-profession-card', `showcase-profession-card--${props.variant}`]">
    <div class="showcase-profession-card__hero">
      <img
        :src="props.profession.backgroundImage"
        :alt="props.profession.name"
        class="showcase-profession-card__hero-img"
        width="320"
        height="180"
        loading="lazy"
        decoding="async"
        @error="onHeroError" />
    </div>
    <div class="showcase-profession-card__body">
      <h3 class="showcase-profession-card__title">{{ props.profession.name }}</h3>
      <div class="showcase-profession-card__info">
        <p
          v-if="props.profession.domains?.length"
          class="showcase-profession-card__info-row">
          <span
            class="showcase-profession-card__info-icon"
            aria-hidden="true"
            >🔗</span
          >
          {{ props.profession.domains.join(', ') }}
        </p>
        <p
          v-if="props.profession.sectors?.length"
          class="showcase-profession-card__info-row">
          <span
            class="showcase-profession-card__info-icon"
            aria-hidden="true"
            >🏢</span
          >
          {{ props.profession.sectors.join(', ') }}
        </p>
        <p
          v-if="props.profession.salary && !props.profession.link"
          class="showcase-profession-card__info-row">
          <span
            class="showcase-profession-card__info-icon"
            aria-hidden="true"
            >💰</span
          >
          {{ props.profession.salary }}
        </p>
        <div
          v-if="props.profession.salary && props.profession.link"
          class="showcase-profession-card__info-row showcase-profession-card__info-row--last">
          <div class="showcase-profession-card__info-left">
            <span
              class="showcase-profession-card__info-icon"
              aria-hidden="true"
              >💰</span
            >
            {{ props.profession.salary }}
          </div>
          <NuxtLink
            :to="props.profession.link"
            class="showcase-profession-card__link">
            Lire la fiche →
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { ShowcaseProfessionCardData } from '~/types/homeShowcases'
import type {
  ShowcaseProfessionCardProps,
  ShowcaseProfessionCardVariant,
} from '~/types/ShowcaseProfessionCard'
import { SHOWCASE_HERO_FALLBACK_BACKGROUND } from '~/utils/homeShowcases'

/**
 * Définit les props du composant ShowcaseProfessionCard.
 */
const props: ShowcaseProfessionCardProps = defineProps({
  profession: {
    type: Object as PropType<ShowcaseProfessionCardData>,
    required: true,
  },
  variant: {
    type: String as PropType<ShowcaseProfessionCardVariant>,
    required: true,
  },
})

/**
 * Applique le dégradé de secours sur le hero si l'image ne charge pas.
 * @param {Event} event - Événement `error` de l'image.
 */
function onHeroError(event: Event): void {
  const target: HTMLImageElement | null = event.target as HTMLImageElement | null
  const parent: HTMLElement | null = target?.parentElement ?? null
  if (!parent) return
  parent.style.background = SHOWCASE_HERO_FALLBACK_BACKGROUND
}
</script>

<style scoped>
.showcase-profession-card {
  position: absolute;
  background: #1a1a2e;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  width: 280px;
  border: 1px solid rgba(124, 58, 237, 0.3);
}

.showcase-profession-card--back {
  top: 0;
  left: 0;
  z-index: 1;
  transform: rotate(-1.5deg);
}

.showcase-profession-card--front {
  top: 100px;
  right: 0;
  z-index: 2;
  transform: rotate(1deg);
}

.showcase-profession-card__hero {
  position: relative;
  height: 180px;
  background: linear-gradient(135deg, #4c1d95, #7c3aed);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  overflow: hidden;
}

.showcase-profession-card__hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
}

.showcase-profession-card__body {
  padding: var(--space-4) var(--space-4) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.showcase-profession-card__title {
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-primary);
}

.showcase-profession-card__info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.showcase-profession-card__info-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.showcase-profession-card__info-row--last {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.showcase-profession-card__info-left {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.showcase-profession-card__info-icon {
  flex-shrink: 0;
  font-size: 0.75rem;
}

.showcase-profession-card__link {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--accent-violet-lt);
  text-decoration: none;
  white-space: nowrap;
  transition: color var(--duration-fast) var(--ease-out);
}

.showcase-profession-card__link:hover {
  color: #fff;
}

@media (max-width: 900px) {
  .showcase-profession-card {
    width: 240px;
  }

  .showcase-profession-card--back {
    left: 0;
  }

  .showcase-profession-card--front {
    right: 0;
    top: 70px;
  }
}

@media (max-width: 480px) {
  .showcase-profession-card {
    width: 200px;
  }

  .showcase-profession-card__hero {
    height: 130px;
  }
}
</style>
